import React, { ReactNode, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { differenceInHours, parseISO } from 'date-fns'
import { isEmpty } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ProviderAvailability } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { Capacity } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'

import { useProviderCapacityAvailability } from './useProviderCapacityAvailability'
import { mixpanelEvents } from '../../../mixpanel/mixpanelConstants'
import { track } from '../../../mixpanel/mixpanelTracking'
import { BCC_ROLES, BCT_ROLES, ROLES } from '../common/constants/appConstants'
import { getTotalCapacity, hasRole } from '../common/utils/utils'
import { getAuthConfig, getAuthUser } from '../data/auth/authSelectors'
import { getRequestPaymentSelectedProvider } from '../data/requestPayment/requestPaymentSelectors'
import { AvailabilityAndCapacityMismatchModal } from '../lyraTherapy/clients/AvailabilityAndCapacityMismatchModal'
import ProviderAvailabilityModal from '../lyraTherapy/clients/ProviderAvailabilityModal'

export type UseCapacityUpdateFlowResult = {
  acceptingCount: number
  currentCapacity: Capacity
  providerAvailabilities: ProviderAvailability[]
  isLoadingAvailability: boolean
  isStale: boolean
  hasMismatch: boolean
  isCapacityProvider: boolean
  isVisible: boolean
  openUpdateFlow: () => void
  modals: ReactNode
}

export const useCapacityUpdateFlow = (): UseCapacityUpdateFlowResult => {
  const user = useSelector(getAuthUser)
  const config = useSelector(getAuthConfig)
  const selectedProvider = useSelector(getRequestPaymentSelectedProvider)
  const provider = selectedProvider || user
  const { shouldShowCapacityToAvailabilityRatioAlert } = useFlags()
  const { data: availabilityData, isLoading: isLoadingAvailability } = useProviderCapacityAvailability()

  const currentCapacity: Capacity = provider?.capacity_value ?? {}
  const acceptingCount = getTotalCapacity(currentCapacity)
  const providerAvailabilities = availabilityData?.provider_availability?.[0]?.availability ?? []

  const availabilityUpdatedAt = provider?.capacity_updated_at
  const isStale = !availabilityUpdatedAt || differenceInHours(new Date(), parseISO(availabilityUpdatedAt)) > 24

  const isCapacityProvider =
    Boolean(config?.bcPlatform) ||
    hasRole(provider?.roles, [ROLES.INDIVIDUAL_PROVIDER, ROLES.PRACTICE_PROVIDER]) ||
    selectedProvider !== undefined

  const hasProperRolesToSeeCapacity = hasRole(provider?.roles, [...BCC_ROLES, ...BCT_ROLES])
  const isVisible = isCapacityProvider && hasProperRolesToSeeCapacity && !isEmpty(currentCapacity)

  const hasMismatch = !hasRole(provider?.roles, [ROLES.LT_PRESCRIBER]) && acceptingCount > providerAvailabilities.length

  const [isCapacityModalOpen, setCapacityModalOpen] = useState(false)
  const [isMismatchModalOpen, setMismatchModalOpen] = useState(false)
  const [mismatchCount, setMismatchCount] = useState(0)

  const openUpdateFlow = useCallback(() => {
    if (!isCapacityProvider) {
      return
    }
    setCapacityModalOpen(true)
    track({ event: 'BUTTON_PRESS', action: 'UPDATE_CAPACITY' })
  }, [isCapacityProvider])

  const handleMismatchModalOpen = useCallback(() => {
    if (!isMismatchModalOpen) {
      setMismatchModalOpen(true)
      track({ event: mixpanelEvents.CAPACITY_AVAILABILITY_ALERT_MODAL_SHOWN })
    }
  }, [isMismatchModalOpen])

  const modals = (
    <>
      <Modal
        onCloseEnd={() => setCapacityModalOpen(false)}
        onRequestClose={() => setCapacityModalOpen(false)}
        disableBottomSheet={true}
        visible={isCapacityModalOpen}
        modalContents={
          <ProviderAvailabilityModal
            currentCapacity={currentCapacity}
            providerId={provider?.id as string}
            selectedProvider={selectedProvider || {}}
            triggerMismatchModal={handleMismatchModalOpen}
            setMismatchModalCount={setMismatchCount}
            providerAvailabilities={providerAvailabilities}
            closeModal={() => setCapacityModalOpen(false)}
          />
        }
        width='518px'
        closeOnScrim
        scrollable
        scrollableModalHeight='100%'
      />
      {shouldShowCapacityToAvailabilityRatioAlert && (
        <AvailabilityAndCapacityMismatchModal
          onClose={() => setMismatchModalOpen(false)}
          isModalOpen={isMismatchModalOpen}
          shortfall={mismatchCount}
        />
      )}
    </>
  )

  return {
    acceptingCount,
    currentCapacity,
    providerAvailabilities,
    isLoadingAvailability,
    isStale,
    hasMismatch,
    isCapacityProvider,
    isVisible,
    openUpdateFlow,
    modals,
  }
}
